// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import isProd from '@utils/isProd';

import { GetSharepointDataAction } from 'constants/BackgroundScript';
import { isSharepointExcelWorkbookTabUrl } from 'constants/SharepointTrustedWorkbookUrls';
import { getCurrentTab } from 'lib/chromeScripting/util';
import { Maybe } from 'types/UtilityTypes';

import type { SharepointAgileRow } from '../types/Sharepoint';
import { isSharepointAgileRow } from '../types/Sharepoint';

/** Normalizes SW cache payload to parsed Agile rows (defensive type guard). */
export function normalizeSharepointAgileRows(
  data: unknown
): SharepointAgileRow[] {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.filter(isSharepointAgileRow);
}

export type SharepointEwaAgileRowsPullResult = {
  hostTabId?: number;
  /** Null when not on a trusted workbook tab, fetch failed, or cache empty. */
  rows: Maybe<SharepointAgileRow[]>;
};

/**
 * Resolves the side-panel host Excel tab and reads the service worker's last EWA prefetch
 * parse for that tab. Does not trigger a new prefetch or rescrape Excel.
 */
export async function pullSharepointEwaAgileRowsFromServiceWorker(): Promise<SharepointEwaAgileRowsPullResult> {
  if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
    return { rows: null };
  }

  try {
    const tab = await getCurrentTab({ preferSidepanelHostTab: true });
    const hostTabId = typeof tab?.id === 'number' ? tab.id : undefined;
    const tabUrl = typeof tab?.url === 'string' ? tab.url.trim() : '';

    if (
      hostTabId == null ||
      !tabUrl ||
      !isSharepointExcelWorkbookTabUrl(tabUrl)
    ) {
      return { hostTabId, rows: null };
    }

    const res = (await chrome.runtime.sendMessage({
      action: GetSharepointDataAction,
      tabId: hostTabId,
    })) as { success?: boolean; data?: unknown };

    if (res?.success === false) {
      return { hostTabId, rows: null };
    }

    const parsed = normalizeSharepointAgileRows(res?.data);
    return {
      hostTabId,
      rows: parsed.length > 0 ? parsed : null,
    };
  } catch (e: unknown) {
    if (!isProd()) {
      console.warn('[Drumkit SharePoint] ewa agile rows pull failed', e);
    }
    return { rows: null };
  }
}
